import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  Popover,
  Stack,
  useMediaQuery,
  useTheme,
  ButtonBase,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router';

import ArrowUpIcon from '@/assets/icons/arrow-up.svg?react';
import HamburgerMenuIcon from '@/assets/icons/hamburger-menu.svg?react';
import UploaderIcon from '@/assets/icons/uploader.svg?react';
import HauserLogoBlack from '@/assets/images/hauser-logo-black.svg?react';
import HauserLogo from '@/assets/images/hauser-logo.svg?react';
import { Dialog, DialogRef } from '@/components/common/Dialog';
import FilePicker, { FilePickerRef } from '@/components/common/FilePicker';
import { ResponsiveMenu, ResponsiveMenuItem } from '@/components/common/ResponsiveMenu';
import { SelectIcon } from '@/components/common/Select';
import {
  EditPropertyDialogRef,
  EditPropertyDialog,
} from '@/components/property/EditPropertyDialog';
import { useAuthContext } from '@/context/AuthProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useProperties } from '@/hooks/properties';
import { useLogger } from '@/hooks/useLogger';
import { useUserRole } from '@/hooks/useUserRole';
import { UserProperty } from '@/models/Property';
import { useTranslationPrefix } from '@/translations/i18n';

export interface TopNavBarRef {
  _unused?: never;
}

export interface TopNavBarProps {
  onFileUploadClick?: () => void;
}

const TopNavBar = forwardRef<TopNavBarRef, TopNavBarProps>(({ onFileUploadClick }, ref) => {
  const { logOut, isLoggingOut } = useAuthContext();
  const { isInternal } = useUserRole();
  const {
    uploadedDocuments,
    startFileUpload,
    isFileUploadInProgress,
    isPropertyProcessingInProgress,
  } = useUploaderContext();
  const navigate = useNavigate();
  const [hamburgerMenuAnchor, setHamburgerMenuAnchor] = useState<null | HTMLElement>(null);
  const [propertyMenuAnchor, setPropertyMenuAnchor] = useState<null | HTMLElement>(null);
  const [propertyActionAnchor, setPropertyActionAnchor] = useState<null | HTMLElement>(null);
  const [selectedActionProperty, setSelectedActionProperty] = useState<UserProperty | null>(null);
  const editPropertyDialogRef = useRef<EditPropertyDialogRef>(null);
  const deleteConfirmDialogRef = useRef<DialogRef>(null);
  const deleteFavoriteAlertDialogRef = useRef<DialogRef>(null);
  const filePickerRef = useRef<FilePickerRef>(null);
  const { showAlert } = useSnackbar();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const t = useTranslationPrefix('top_nav_bar');
  const logger = useLogger('TopNavBar');

  const {
    properties,
    isFetchingProperties,
    selectedProperty,
    setSelectedProperty,
    selectFavoriteProperty,
  } = usePropertiesContext();
  const { markAsFavorite, deleteProperty, isFetching, isMarkingAsFavorite, isDeleting } =
    useProperties();
  const [userProperties, setUserPropertes] = useState<UserProperty[]>([]);

  const handleHamburgerMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHamburgerMenuAnchor(event.currentTarget);
  };

  const openExternalLink = (url: string, newWindow = true) => {
    if (newWindow) {
      window.open(url);
    } else {
      window.location.href = url;
    }
  };

  const handleFileUploadClick = () => {
    if (
      uploadedDocuments.length === 0 &&
      !isFileUploadInProgress &&
      !isPropertyProcessingInProgress
    ) {
      filePickerRef.current?.openPickerDialog();
      return;
    }
    onFileUploadClick?.();
  };

  const handleFilePickerChange = (files: File[]) => {
    if (files.length === 0) {
      return;
    }
    startFileUpload(files);
  };

  const handlePropertyMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPropertyMenuAnchor(event.currentTarget);
  };

  const handlePropertySelected = (property: UserProperty) => {
    setSelectedProperty(property);

    // check if not on the dashbaord, then navigate to dashboard to see the selected property
    if (window.location.pathname !== '/') {
      navigate('/', { state: { preselectedProperty: property } });
    }
  };

  const handlePropertySettingsClick = (
    event: React.MouseEvent<HTMLElement>,
    property: UserProperty,
  ) => {
    event.stopPropagation();
    setSelectedActionProperty(property);
    setPropertyActionAnchor(event.currentTarget);
  };

  const handleHauserLogoClick = () => {
    if (userProperties.length === 0) return;
    navigate('/');
  };

  const handleMarkPropertyAsFavorite = (
    event: React.MouseEvent<HTMLElement>,
    property: UserProperty,
  ) => {
    event.stopPropagation();
    if (property.isFavorite) {
      return;
    }
    markAsFavorite(property.property.id);
  };

  const handleClosePropertyActions = () => {
    setPropertyActionAnchor(null);
  };

  const handleEditProperty = () => {
    handleClosePropertyActions();
    editPropertyDialogRef.current?.open();
  };

  const handleDeleteProperty = () => {
    if (selectedActionProperty?.isFavorite) {
      deleteFavoriteAlertDialogRef.current?.open();
      return;
    }
    deleteConfirmDialogRef.current?.open();
  };

  const confirmDeleteProperty = () => {
    if (!selectedActionProperty) {
      return;
    }

    const currentDialogRef = deleteConfirmDialogRef.current;

    try {
      deleteProperty(selectedActionProperty.property.id, {
        onSuccess: () => {
          currentDialogRef?.close();
          handleClosePropertyActions();

          showAlert(t('property_removed_success'), 'success');

          // if the deleted property was the selected one, set the selected property to favorite
          if (selectedProperty?.property.id === selectedActionProperty.property.id) {
            selectFavoriteProperty();
          }
        },
        onError: (error) => {
          logger.error('Error deleting property:', error);
          showAlert(t('property_removed_error'), 'error');
        },
      });
    } catch (error) {
      showAlert(t('property_removed_error'), 'error');
      logger.error('Error deleting property', { error });
    }
  };

  const onUploaderClick = () => {
    navigate('/uploader');
  };

  const cancelDeleteProperty = () => {
    deleteConfirmDialogRef.current?.close();
  };

  const closeDeleteFavoriteAlert = () => {
    deleteFavoriteAlertDialogRef.current?.close();
  };

  useImperativeHandle(ref, () => ({}));

  useEffect(() => {
    if (!properties || isFetchingProperties) return;

    const arePropertiesRemoved = userProperties.length > properties.length;
    const arePropertiesSameLength = userProperties.length === properties.length;

    // sort by isFavorite first, then by createdAt
    const sortedProperties = [...properties].sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return new Date(b.property.createdAt).getTime() - new Date(a.property.createdAt).getTime();
    });
    setUserPropertes(sortedProperties);

    // Initial selection (incl. restoring last-used from localStorage) is owned
    // by PropertiesProvider — don't race with it here.

    if (arePropertiesRemoved) {
      // if a property was removed, check if the selected property is still in the list
      // if not, select the first property in the list
      if (
        !properties
          .map((property) => property.property.id)
          .includes(selectedProperty?.property.id || '')
      ) {
        setSelectedProperty(properties[0]);
      }
    }

    if (arePropertiesSameLength) {
      // select the same property in case it's been changed
      const property = properties.find((p) => p.property.id === selectedProperty?.property.id);
      if (property) {
        setSelectedProperty(property);
      }
    }
  }, [properties, isFetchingProperties]);

  return (
    <>
      {isMobile ? (
        <MobileAppBar
          selectedProperty={selectedProperty}
          onLogoClick={handleHauserLogoClick}
          onHamburgerMenuClick={handleHamburgerMenuOpen}
          onPropertyMenuClick={handlePropertyMenuOpen}
          onUploaderClick={onUploaderClick}
        />
      ) : (
        <DesktopAppBar
          userProperties={userProperties}
          selectedProperty={selectedProperty}
          onLogoClick={handleHauserLogoClick}
          onHamburgerMenuClick={handleHamburgerMenuOpen}
          onPropertyMenuClick={handlePropertyMenuOpen}
          onFileUploadClick={handleFileUploadClick}
        />
      )}

      {/* Menus */}
      <ResponsiveMenu
        anchorEl={hamburgerMenuAnchor}
        open={Boolean(hamburgerMenuAnchor)}
        onClose={() => setHamburgerMenuAnchor(null)}
        mobileTitle={t('hamburger_menu.title')}
        transformOrigin={{ vertical: -2, horizontal: 'left' }}>
        {userProperties.length > 0 && (
          <ResponsiveMenuItem onClick={() => navigate('/')}>
            {t('hamburger_menu.home')}
          </ResponsiveMenuItem>
        )}
        <ResponsiveMenuItem onClick={() => openExternalLink('https://www.hauserhq.com/support')}>
          {t('hamburger_menu.support')}
        </ResponsiveMenuItem>
        <ResponsiveMenuItem onClick={() => openExternalLink('https://www.hauserhq.com/invite')}>
          {t('hamburger_menu.invite')}
        </ResponsiveMenuItem>
        <ResponsiveMenuItem
          onClick={() =>
            navigate('/user/update-profile', { state: { redirectToHomeOnProfileUpdate: false } })
          }>
          {t('hamburger_menu.my_account')}
        </ResponsiveMenuItem>
        <ResponsiveMenuItem isLoading={isLoggingOut} onClick={() => logOut()}>
          {t('hamburger_menu.logout')}
        </ResponsiveMenuItem>
      </ResponsiveMenu>

      {userProperties && (
        <Menu
          anchorEl={propertyMenuAnchor}
          open={Boolean(propertyMenuAnchor)}
          onClose={() => setPropertyMenuAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            paper: {
              sx: {
                width: { sm: 350, md: 650 },
                maxHeight: 400,
                mt: 1,
                borderRadius: 6,
                p: 2,
                '& .MuiList-root': {
                  py: 1,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 2,
                },
              },
            },
          }}>
          {userProperties.map((userProperty) => {
            const property = userProperty.property;
            const isSelected = selectedProperty?.property.id === property.id;
            const isFavorite = userProperty.isFavorite;
            return (
              <MenuItem
                key={property.id}
                onClick={() => {
                  handlePropertySelected(userProperty);
                  setPropertyMenuAnchor(null);
                }}
                sx={{
                  py: 4,
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  backgroundColor: isSelected ? 'action.hover' : 'transparent',
                  height: '100%',
                  position: 'relative',
                  border: 'none',
                  borderRadius: 2.5,
                }}>
                <Stack width="100%">
                  <Stack
                    direction="row"
                    mb={1}
                    ml={-1}
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}>
                    <FavoriteIconButton
                      userProperty={userProperty}
                      isFavorite={isFavorite}
                      isSelected={isSelected}
                      isLoading={isMarkingAsFavorite || isFetching}
                      onMarkPropertyAsFavorite={handleMarkPropertyAsFavorite}
                    />
                    {isFavorite && (
                      <Typography
                        fontSize={10}
                        fontWeight={500}
                        sx={{
                          bgcolor: 'success2.main',
                          color: 'primary.contrastText',
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          whiteSpace: 'nowrap',
                        }}>
                        {t('primary').toUpperCase()}
                      </Typography>
                    )}
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '80%',
                      fontWeight: isFavorite ? 600 : 'regular',
                    }}>
                    {property.address}
                  </Typography>

                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '140px',
                      }}>
                      {property.name}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{
                        p: 0.5,
                        ml: 1,
                        color: 'text.secondary',
                      }}
                      onClick={(e) => handlePropertySettingsClick(e, userProperty)}>
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </MenuItem>
            );
          })}
        </Menu>
      )}

      {/* Property Actions Popover */}
      <Popover
        open={Boolean(propertyActionAnchor)}
        anchorEl={propertyActionAnchor}
        onClose={handleClosePropertyActions}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPopover-paper': {
            width: 200,
            borderRadius: 2,
            mt: 1,
            backgroundColor: 'background.paper',
            p: 2,
          },
        }}>
        <MenuItem onClick={handleEditProperty} sx={{ py: 1.5, border: 'none', px: 2 }}>
          <Typography variant="body2">{t('edit_property')}</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleDeleteProperty}
          disabled={!isInternal}
          sx={{ py: 1.5, border: 'none', px: 2 }}>
          <Typography variant="body2">{t('remove_property')}</Typography>
        </MenuItem>
      </Popover>

      <Dialog
        ref={deleteConfirmDialogRef}
        containerProps={{ p: 6 }}
        title={t('remove_property_title')}
        actions={
          <>
            <Button
              fullWidth
              variant="outlined"
              disabled={isDeleting}
              onClick={cancelDeleteProperty}>
              {t('cancel')}
            </Button>
            <Button fullWidth loading={isDeleting} onClick={confirmDeleteProperty}>
              {isDeleting ? t('removing') : t('remove')}
            </Button>
          </>
        }
        disableClose={isDeleting}>
        <Stack spacing={5}>
          <Typography variant="body2" color="text.secondary">
            {t('remove_property_confirmation.0')}
            <Typography component="span" variant="inherit" fontWeight="600">
              {t('remove_property_confirmation.1')}
            </Typography>
            {t('remove_property_confirmation.2')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('remove_property_confirmation.3')}
          </Typography>
        </Stack>
      </Dialog>

      <Dialog
        ref={deleteFavoriteAlertDialogRef}
        containerProps={{ p: 6 }}
        title={t('remove_favorite_property_title')}
        subtitle={t('remove_favorite_property_description')}
        actions={
          <Button variant="outlined" onClick={closeDeleteFavoriteAlert}>
            {t('go_back')}
          </Button>
        }
      />

      {selectedActionProperty && (
        <EditPropertyDialog
          ref={editPropertyDialogRef}
          property={selectedActionProperty.property}
        />
      )}

      <FilePicker ref={filePickerRef} onChange={handleFilePickerChange} />
    </>
  );
});

const MobileAppBar = ({
  selectedProperty,
  onLogoClick,
  onHamburgerMenuClick,
  onPropertyMenuClick,
  onUploaderClick,
}: {
  selectedProperty?: UserProperty;
  onLogoClick?: () => void;
  onHamburgerMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onPropertyMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onUploaderClick?: (event: React.MouseEvent<HTMLElement>) => void;
}) => {
  const theme = useTheme();

  return (
    <AppBar position="static" sx={{ backgroundColor: 'transparent' }}>
      <Toolbar
        sx={{
          gap: 7,
          [theme.breakpoints.up('sm')]: {
            minHeight: 80,
          },
        }}>
        {/* Left section */}
        <Box flex={1}>
          <IconButton color="primary" aria-label="menu" onClick={onHamburgerMenuClick}>
            <HamburgerMenuIcon width="20px" height="17px" fill={theme.palette.background.paper} />
          </IconButton>
        </Box>

        {/* Center section */}
        <Box width={129} height={25} onClick={onLogoClick} style={{ cursor: 'pointer' }}>
          <HauserLogoBlack width="100%" height="100%" />
        </Box>

        {/* Right section */}
        <Box flex={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={onUploaderClick}>
            <UploaderIcon width={30} height={30} />
          </IconButton>
        </Box>
      </Toolbar>
      <PropertySelect selectedProperty={selectedProperty} onOpenClick={onPropertyMenuClick} />
    </AppBar>
  );
};

const DesktopAppBar = ({
  userProperties,
  selectedProperty,
  onLogoClick,
  onHamburgerMenuClick,
  onPropertyMenuClick,
  onFileUploadClick,
}: {
  userProperties: UserProperty[];
  selectedProperty?: UserProperty;
  onLogoClick?: () => void;
  onHamburgerMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onPropertyMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onFileUploadClick?: (event: React.MouseEvent<HTMLElement>) => void;
}) => (
  <AppBar position="static" sx={{ borderRadius: 25 }}>
    <Toolbar sx={{ gap: 7 }}>
      {/* Left section */}
      <Box flex={1}>
        <Stack
          spacing={7}
          direction="row"
          sx={{
            alignItems: 'center',
            pl: { sm: 4 },
          }}>
          <IconButton size="large" color="primary" aria-label="menu" onClick={onHamburgerMenuClick}>
            <HamburgerMenuIcon />
          </IconButton>
          <Box width={129} height={25} onClick={onLogoClick} style={{ cursor: 'pointer' }}>
            <HauserLogo width="100%" height="100%" />
          </Box>
        </Stack>
      </Box>

      {/* Center section */}
      <Box width={{ md: 394, lg: 500 }}>
        {userProperties.length > 0 && (
          <PropertySelect selectedProperty={selectedProperty} onOpenClick={onPropertyMenuClick} />
        )}
      </Box>

      {/* Right section */}
      <Box flex={1}>
        <Stack
          spacing={1.5}
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}>
          <IconButton onClick={onFileUploadClick} sx={{ borderRadius: 25 }}>
            <Box
              sx={{
                width: { sm: 35, lg: 78 },
                height: 35,
                display: 'flex',
                alignItems: 'center',
                justifyContent: { sm: 'center', lg: 'flex-end' },
                backgroundColor: 'success2.main',
                borderRadius: 25,
                pr: { sm: 0, lg: 2.5 },
              }}>
              <ArrowUpIcon width={19} height={23} />
            </Box>
          </IconButton>
        </Stack>
      </Box>
    </Toolbar>
  </AppBar>
);

const PropertySelect = ({
  selectedProperty,
  onOpenClick,
}: {
  selectedProperty?: UserProperty;
  onOpenClick?: (event: React.MouseEvent<HTMLElement>) => void;
}) => {
  const t = useTranslationPrefix('top_nav_bar');

  return (
    <>
      <ButtonBase
        component="div"
        onClick={onOpenClick}
        sx={(theme) => ({
          width: '100%',
          height: { sm: 44, md: 35 },
          alignItems: 'center',
          backgroundColor: 'background.neutral',
          transition: theme.transitions.create(['background-color']),
          pl: { sm: 7.5, lg: 9.5 },
          pr: { sm: 10, lg: 15 },
          mx: 'auto',
          borderRadius: 50,
          '&:hover': {
            backgroundColor: lighten(theme.palette.background.neutral, 0.05),
          },
        })}>
        <Stack spacing={2} direction="row">
          <Typography variant="body2" noWrap sx={{ flex: 1, textAlign: 'center' }}>
            {selectedProperty ? selectedProperty.property.address : t('search')}
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              pr: { sm: 4, lg: 8 },
            }}>
            <SelectIcon />
          </Box>
        </Stack>
      </ButtonBase>
    </>
  );
};

const FavoriteIconButton: React.FC<{
  userProperty: UserProperty;
  isFavorite: boolean;
  isSelected: boolean;
  isLoading: boolean;
  onMarkPropertyAsFavorite: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    userProperty: UserProperty,
  ) => void;
}> = ({ userProperty, isFavorite, isSelected, isLoading, onMarkPropertyAsFavorite }) => (
  <IconButton
    onClick={(event) => onMarkPropertyAsFavorite(event, userProperty)}
    loading={isLoading}
    sx={{
      p: 0.5,
      mr: 1,
      color: isSelected ? 'primary.main' : 'text.secondary',
    }}>
    {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
  </IconButton>
);

export default TopNavBar;
